import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { ImagesStorageService } from '../services/images-storage.service';
import { Subscription } from 'rxjs';
import { PagingResult } from 'src/app/shared/models/pageable';
import { ImgWithSelection } from '../model/img-with-selection';
import { ImageSortingOptions } from '../img-db.component';
import { FormControl } from '@angular/forms';
import { ImgDbZoomService } from '../services/img-db-zoom.service';
import { ToastMessageService } from '../../shared/services/toast-message.service';
import { isNil } from '../../shared/utility';

export type pagingType = 'overview' | 'annotate';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false,
})
export class FooterComponent implements OnInit, OnDestroy, OnChanges {
    currentPage: number = 1;
    subscriptions: Subscription[] = [];
    totalPages: number;
    zoomControl: FormControl = new FormControl();
    ZoomService = ImgDbZoomService;
    @Input() type: pagingType;
    @Input() sortingOption: ImageSortingOptions;
    @Input() selectedImage: ImgWithSelection;

    @Output() zoomEmitter: EventEmitter<string> = new EventEmitter<string>();
    readonly TOAST_MESSAGE_DELAY = { delay: 2000 };

    constructor(
        private storage: ImagesStorageService,
        private zoomService: ImgDbZoomService,
        private messageService: ToastMessageService
    ) {}

    ngOnInit(): void {
        if (this.isInOverview()) {
            this.subscriptions.push(
                this.storage.imagesPage$.subscribe(
                    (page: PagingResult<ImgWithSelection>) => {
                        if (page) {
                            this.totalPages = page.totalPages;
                            this.currentPage = this.storage.currentPage;
                        }
                    }
                )
            );
            this.subscriptions.push(
                this.zoomService.imagesInRowManageDialog.subscribe(
                    (zoomLevel: number) => {
                        this.zoomControl.setValue(
                            ImgDbZoomService.MAX_IMAGES_PER_ROW - zoomLevel
                        );
                    }
                )
            );
        } else if (this.isInAnnotate()) {
            this.subscriptions.push(
                this.storage.imagesPage$.subscribe(
                    (page: PagingResult<ImgWithSelection>) => {
                        if (page) {
                            this.totalPages = page.totalElements;
                        }
                    }
                )
            );
            this.subscriptions.push(
                this.storage.selectedImage$.subscribe(
                    (image: ImgWithSelection) => {
                        if (image) {
                            this.currentPage =
                                (this.storage.currentPage - 1) *
                                    this.storage.pageSize +
                                this.storage.imagesPage$.value.values.findIndex(
                                    (img: ImgWithSelection) =>
                                        img.id === image.id
                                ) +
                                1;
                        }
                    }
                )
            );
            this.subscriptions.push(
                this.zoomService.zoomLevelLabelDialog.subscribe(
                    (zoomLevel: number) => {
                        this.zoomControl.setValue(zoomLevel);
                    }
                )
            );
        }
    }

    ngOnChanges(): void {
        this.ngOnDestroy();
        this.ngOnInit();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
    }

    onNextValue(numberProvidedFromPagingBar: number) {
        if (this.currentPage !== numberProvidedFromPagingBar) {
            if (this.isInOverview()) {
                this.currentPage = numberProvidedFromPagingBar;
                this.storage.changePage(this.currentPage);
                this.storage.getImages(true, this.sortingOption).subscribe();
            } else if (this.isInAnnotate()) {
                this.currentPage = numberProvidedFromPagingBar;

                const indexOfImageToBeSelected =
                    this.findIndexOfImageToBeSelected(this.currentPage) - 1;

                const newPage = this.calculateNextPage(
                    numberProvidedFromPagingBar
                );
                if (newPage !== this.storage.currentPage) {
                    this.storage.changePage(newPage);
                    this.storage
                        .getImages(true, this.sortingOption)
                        .subscribe();
                }
                // Always queue the selection. The storage will apply it
                // immediately when imagesPage$ already holds the requested
                // page, or defer it until the in-flight fetch above lands.
                // Concurrent calls collapse to the most recent intent, so
                // a quick second click can't be overridden by an older
                // fetch's subscribe callback.
                this.storage.updateImageSelection(indexOfImageToBeSelected);
            }
        }
    }

    private calculateNextPage(val: number): number {
        return Math.ceil(val / this.storage.pageSize);
    }

    private findIndexOfImageToBeSelected(imageToGet: number): number {
        return imageToGet % this.storage.pageSize || this.storage.pageSize;
    }

    onZoomIn() {
        this.zoomEmitter.emit('in');
    }

    onZoomOut() {
        this.zoomEmitter.emit('out');
    }

    zoomChange() {
        let zoomLevel = this.zoomControl.value;
        if (this.isInOverview()) {
            zoomLevel = ImgDbZoomService.MAX_IMAGES_PER_ROW - zoomLevel;
        }
        this.zoomEmitter.emit('' + zoomLevel);
    }

    get minZoom(): number {
        return this.isInOverview() ? 0 : ImgDbZoomService.MIN_IMAGE_ZOOM;
    }

    get maxZoom(): number {
        return this.isInOverview()
            ? ImgDbZoomService.MAX_IMAGES_PER_ROW -
                  ImgDbZoomService.MIN_IMAGES_PER_ROW
            : ImgDbZoomService.MAX_IMAGE_ZOOM - 1;
    }

    isInOverview(): boolean {
        return this.type === 'overview';
    }

    isInAnnotate(): boolean {
        return this.type === 'annotate';
    }

    async onCopyImageName() {
        await this.copyTextToClipboard(this.selectedImage?.name, 'name');
    }

    async onCopyImageId() {
        await this.copyTextToClipboard(this.selectedImage?.id, 'ID');
    }

    private async copyTextToClipboard(text: string, label: string) {
        if (isNil(text)) return;

        try {
            await navigator.clipboard.writeText(text);
            this.messageService.showSuccessMsg(
                `Copied ${label} to clipboard`,
                this.TOAST_MESSAGE_DELAY
            );
        } catch (_) {
            this.messageService.showErrorMsg(
                `Failed to copy ${label}`,
                this.TOAST_MESSAGE_DELAY
            );
        }
    }
}
